import React from "react";
import {
  Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, Tooltip, Typography, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LegacyTokens, B13, B13Ink } from "../../../theme/b13Tokens";
import { alpha } from "@mui/material/styles";
import {
  useListProgramPromptLevelCustomizationsQuery,
  useSaveProgramPromptLevelCustomizationsMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

// Batch 10 §10.3 — restricted per-program prompt-level editor.
// Only VISIBILITY (eye icon) and ORDER (up/down chevrons) are editable.
// Template name, colour swatch, and level name are read-only. No trash,
// no outcome toggle, no advanced settings. Hidden levels collapse to a
// "Hidden Prompt Levels" accordion at the bottom, restorable from there.
//
// The TIP banner copy is verbatim from the spec:
//   "Only prompt level visibility and order can be changed. Everything
//    else (name, colour, scoring, direction) stays with the template."

type Item = {
  promptLevelId: number;
  levelName: string;
  shortCode: string | null;
  colour: string | null;
  customOrder: number;
  isHidden: boolean;
  templateOrder: number;
  hasOverride: boolean;
};

export type CustomizePromptLevelsModalProps = {
  open: boolean;
  programId: number | string;
  onClose: () => void;
};

export default function CustomizePromptLevelsModal({
  open, programId, onClose,
}: CustomizePromptLevelsModalProps) {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isFetching, refetch } = useListProgramPromptLevelCustomizationsQuery(programId, { skip: !open });
  const [saveMutation, { isLoading: saving }] = useSaveProgramPromptLevelCustomizationsMutation();

  const [items, setItems] = React.useState<Item[]>([]);
  const [hiddenOpen, setHiddenOpen] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    const d = data?.data as { items?: Item[] } | undefined;
    if (open && d?.items) {
      setItems(d.items.map((i) => ({ ...i })));
      setDirty(false);
    }
  }, [open, data]);

  // Split into visible (in order) and hidden (order preserved but bucketed).
  const visible = items.filter((i) => !i.isHidden);
  const hidden  = items.filter((i) =>  i.isHidden);

  const moveVisible = (from: number, to: number) => {
    if (to < 0 || to >= visible.length) return;
    const c = [...visible];
    const [x] = c.splice(from, 1);
    c.splice(to, 0, x);
    // Rewrite customOrder contiguously across visible first, then append
    // hidden preserving their relative order after the visible block.
    // Hidden items stay in the "Hidden" bucket in the UI, but their
    // customOrder is written past the visible ones for persistence
    // stability — if a hidden item is restored later, it lands at the
    // end of the visible list.
    let cursor = 0;
    const next = [
      ...c.map((it) => ({ ...it, customOrder: cursor++ })),
      ...hidden.map((it) => ({ ...it, customOrder: cursor++ })),
    ];
    setItems(next);
    setDirty(true);
  };

  const toggleHidden = (promptLevelId: number) => {
    const next = items.map((it) =>
      it.promptLevelId === promptLevelId ? { ...it, isHidden: !it.isHidden } : it
    );
    // Recompute customOrder so visible items are contiguous starting at 0.
    let cursor = 0;
    const rewritten = [
      ...next.filter((i) => !i.isHidden).map((it) => ({ ...it, customOrder: cursor++ })),
      ...next.filter((i) =>  i.isHidden).map((it) => ({ ...it, customOrder: cursor++ })),
    ];
    setItems(rewritten);
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      const res: any = await saveMutation({
        programId,
        items: items.map((it) => ({
          promptLevelId: it.promptLevelId,
          customOrder: it.customOrder,
          isHidden: it.isHidden,
        })),
      }).unwrap();
      if (res?.status) {
        showMessage("Prompt level customizations saved.");
        setDirty(false);
        refetch();
        onClose();
      } else {
        showErrorMessage(res?.message || "Save failed.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Save failed.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { width: 560, maxWidth: "calc(100vw - 32px)", borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: `1px solid ${LegacyTokens.line}` }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: LegacyTokens.ink, flexGrow: 1 }}>
          Customize prompt levels
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: LegacyTokens.ink3 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* TIP banner — copy per §10.3 spec. Emerald border, italic label,
           reinforces WHY the surface is restricted so a clinician doesn't
           expect to change score or colour here. */}
        <Box
          sx={{
            p: 1.5, mb: 2,
            border: `1px solid ${LegacyTokens.emerald}`,
            borderRadius: `${LegacyTokens.radius.card}px`,
            backgroundColor: alpha(LegacyTokens.emerald, 0.06),
            display: "flex", alignItems: "flex-start", gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: LegacyTokens.emerald, flexShrink: 0, lineHeight: 1.9 }}>
            Tip
          </Typography>
          <Typography sx={{ fontSize: 13, color: B13Ink.ink2, lineHeight: 1.55 }}>
            Only prompt level visibility and order can be changed. Everything else
            (name, colour, scoring, direction) stays with the template.
          </Typography>
        </Box>

        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Drag-reorder wrapper — visible band only. Hidden items
                sit in their own accordion below and are ordered
                deterministically after the visible band on save
                (moveVisible enforces the two-band cursor), so they are
                intentionally NOT sortable. onDragEnd delegates to the
                existing moveVisible so drag and chevrons share exactly
                one reindex code path — the customOrder write pattern
                is identical to the pre-Batch-N chevron behaviour. */}
            {visible.length === 0 ? (
              <Stack spacing={0.75}>
                <Typography sx={{ fontSize: 13, color: B13Ink.ink3, fontStyle: "italic", py: 2, textAlign: "center" }}>
                  All prompt levels are hidden. Restore one from below.
                </Typography>
              </Stack>
            ) : (
              <SortableVisibleStack
                visibleIds={visible.map((it) => String(it.promptLevelId))}
                onReorder={(from, to) => moveVisible(from, to)}
              >
                {visible.map((it, i) => (
                  <SortablePromptLevelRow
                    key={it.promptLevelId}
                    sortableId={String(it.promptLevelId)}
                    item={it}
                    onMoveUp={() => moveVisible(i, i - 1)}
                    onMoveDown={() => moveVisible(i, i + 1)}
                    onToggleHidden={() => toggleHidden(it.promptLevelId)}
                    canMoveUp={i > 0}
                    canMoveDown={i < visible.length - 1}
                  />
                ))}
              </SortableVisibleStack>
            )}

            {/* Hidden accordion — collapsible per spec. Rows restored via
               the eye icon (same toggle, opposite direction). */}
            <Box
              onClick={() => setHiddenOpen((v) => !v)}
              role="button" tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setHiddenOpen((v) => !v);
                }
              }}
              sx={{
                mt: 2, display: "flex", alignItems: "center", gap: 0.5,
                fontSize: 12, fontWeight: 700, color: B13Ink.ink3,
                cursor: "pointer", userSelect: "none",
                textTransform: "uppercase", letterSpacing: "0.06em",
                "&:hover": { color: LegacyTokens.ink },
              }}
            >
              {hiddenOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              Hidden prompt levels ({hidden.length})
            </Box>
            <Collapse in={hiddenOpen} unmountOnExit>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {hidden.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: B13Ink.ink3, fontStyle: "italic", pl: 3 }}>
                    Nothing hidden.
                  </Typography>
                ) : (
                  hidden.map((it) => (
                    <PromptLevelRow
                      key={it.promptLevelId}
                      item={it}
                      onMoveUp={() => {}}
                      onMoveDown={() => {}}
                      onToggleHidden={() => toggleHidden(it.promptLevelId)}
                      canMoveUp={false}
                      canMoveDown={false}
                    />
                  ))
                )}
              </Stack>
            </Collapse>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${LegacyTokens.line}` }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          variant="contained"
          sx={{ backgroundColor: B13.primary, "&:hover": { backgroundColor: B13.primary } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// SortableVisibleStack — wraps the visible band in DndContext +
// SortableContext. onReorder receives the two indices of the visible
// list only; caller routes them through moveVisible which owns the
// two-band customOrder rewrite (visible first, hidden appended).
const SortableVisibleStack: React.FC<{
  visibleIds: string[];
  onReorder: (from: number, to: number) => void;
  children: React.ReactNode;
}> = ({ visibleIds, onReorder, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = visibleIds.indexOf(String(active.id));
    const to = visibleIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(from, to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
        <Stack spacing={0.75}>{children}</Stack>
      </SortableContext>
    </DndContext>
  );
};

// SortablePromptLevelRow — adds the drag handle and sortable ref +
// transform to a visible-band row. Hidden rows use PromptLevelRow as
// before (no drag affordance in the hidden accordion).
const SortablePromptLevelRow: React.FC<{
  sortableId: string;
  item: Item;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleHidden: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}> = ({ sortableId, item, onMoveUp, onMoveDown, onToggleHidden, canMoveUp, canMoveDown }) => {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: sortableId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : undefined,
    zIndex: isDragging ? 2 : undefined,
    boxShadow: isDragging ? "0 6px 16px rgba(0,0,0,0.12)" : undefined,
  };
  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row" alignItems="center" spacing={1}
      sx={{
        px: 1.25, py: 0.75,
        border: `1px solid ${isDragging ? B13.primary : LegacyTokens.line}`,
        borderRadius: `${LegacyTokens.radius.control}px`,
        backgroundColor: LegacyTokens.surface,
      }}
    >
      {/* Drag handle — first cell to match Surface 1's convention.
          Listeners live only on the handle IconButton so the visibility
          toggle and chevrons keep their own click targets. */}
      <Tooltip title="Drag to reorder (Space to pick up, arrows to move, Space to drop)" arrow>
        <span>
          <IconButton
            size="small"
            aria-label={`Reorder ${item.levelName}`}
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            sx={{
              color: LegacyTokens.ink3,
              cursor: isDragging ? "grabbing" : "grab",
              touchAction: "none",
              "&:hover": { color: B13.primary },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Box
        sx={{
          width: 18, height: 18, borderRadius: "50%",
          backgroundColor: item.colour || B13Ink.ink3,
          flexShrink: 0,
        }}
        aria-hidden
      />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: LegacyTokens.ink, flexGrow: 1 }}>
        {item.levelName}
        {item.shortCode && (
          <Typography component="span" sx={{ fontSize: 12, color: B13Ink.ink3, ml: 1 }}>
            ({item.shortCode})
          </Typography>
        )}
      </Typography>
      <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp} aria-label="Move up">
        <KeyboardArrowUpIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onMoveDown} disabled={!canMoveDown} aria-label="Move down">
        <KeyboardArrowDownIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={onToggleHidden}
        aria-label="Hide"
        sx={{ color: LegacyTokens.ink }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

const PromptLevelRow: React.FC<{
  item: Item;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleHidden: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}> = ({ item, onMoveUp, onMoveDown, onToggleHidden, canMoveUp, canMoveDown }) => (
  <Stack
    direction="row" alignItems="center" spacing={1}
    sx={{
      px: 1.25, py: 0.75,
      border: `1px solid ${LegacyTokens.line}`,
      borderRadius: `${LegacyTokens.radius.control}px`,
      backgroundColor: item.isHidden ? B13.canvas : LegacyTokens.surface,
      opacity: item.isHidden ? 0.7 : 1,
    }}
  >
    {/* Read-only colour swatch — template-scoped */}
    <Box
      sx={{
        width: 18, height: 18, borderRadius: "50%",
        backgroundColor: item.colour || B13Ink.ink3,
        flexShrink: 0,
      }}
      aria-hidden
    />
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: LegacyTokens.ink, flexGrow: 1 }}>
      {item.levelName}
      {item.shortCode && (
        <Typography component="span" sx={{ fontSize: 12, color: B13Ink.ink3, ml: 1 }}>
          ({item.shortCode})
        </Typography>
      )}
    </Typography>
    {!item.isHidden && (
      <>
        <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp} aria-label="Move up">
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onMoveDown} disabled={!canMoveDown} aria-label="Move down">
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
      </>
    )}
    <IconButton
      size="small"
      onClick={onToggleHidden}
      aria-label={item.isHidden ? "Show" : "Hide"}
      sx={{ color: item.isHidden ? B13Ink.ink3 : LegacyTokens.ink }}
    >
      {item.isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
    </IconButton>
  </Stack>
);
